/**
 * Implantaciones — lista (implantaciones.html): las casas con su nombre,
 * sector, sabor y recuento de directivos REALES (API, §5: metadatos) y sus
 * números de memoria REALES; estado derivado (estado.ts). De la semilla
 * queda solo lo que la API aún no sirve: las fuentes conectadas (F5c) — una
 * casa real nueva no tiene ninguna y así se pinta. Paginación real de 12 con
 * altura fijada para que el paginador no salte. Estados del contrato +
 * ?estado= de revisión.
 */
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { EngagementSummary } from "@skein/contracts";
import { Badge, BadgeGroup, Banner, Button, CellTitle, Count, Empty, Fainted, Mono, PageBtn, PageEllipsis, PageHead, Pagination, RowActions, SearchInput, Select, Skeleton, Table, TdNum, Th, Toolbar, useSlashFocus, useToast, type SortDir } from "@skein/ui";
import { useEngagements } from "../../api/queries";
import { metaCasa, nombreCasa } from "./casa";
import { casaDe } from "./casas.seed";
import { estadoDe, type EstadoCasa } from "./estado";
import { fechaTabla } from "./fecha";

const POR_PAG = 12;
type Row = EngagementSummary & { nombre: string; meta: string; bc: boolean; pbi: boolean; estadoCasa: EstadoCasa };
type SortKey = "nombre" | "criterios" | "pendientesDeConfirmar" | "decisiones" | "followUpsAbiertos" | "ts";
const ESTADOS = [
  { value: "", label: "Todos los estados" }, { value: "activa", label: "activas" },
  { value: "onboarding", label: "en onboarding" }, { value: "parada", label: "sin actividad 14d" },
] as const;
const FUENTES = [
  { value: "", label: "Todas las fuentes" }, { value: "bc", label: "con Business Central" },
  { value: "pbi", label: "con Power BI" }, { value: "ninguna", label: "sin fuente conectada" },
] as const;

function ChipEstado({ e }: { e: EstadoCasa }) {
  if (e === "activa") return <Badge tone="ok">activa</Badge>;
  if (e === "onboarding") return <Badge tone="accent">onboarding</Badge>;
  return <Badge tone="warn">sin actividad 14d</Badge>;
}
/** Las conexiones aún no existen (llegan con F5c): lo que se pinta es la
 *  semilla de unas pocas casas demo y el tip no lo esconde. */
function ChipFuentes({ r }: { r: Row }) {
  if (!r.bc && !r.pbi) return <Fainted>—</Fainted>;
  return <BadgeGroup>{r.bc && <Badge tip="Business Central · conexión de fase dev">BC</Badge>}{r.pbi && <Badge tip="Power BI · conexión de fase dev">PBI</Badge>}</BadgeGroup>;
}
const HEADS_PLAIN = <tr><Th>Implantación</Th><Th>Estado</Th><Th>Fuentes</Th><Th num>Criterios</Th><Th num>Pendientes</Th><Th num>Decisiones</Th><Th num>Seguimientos</Th><Th>Última actividad</Th><Th aria-label="Acciones" /></tr>;
function SkeletonRows() {
  const c = (w: string, right?: boolean) => <Skeleton style={{ width: w, ...(right ? { marginLeft: "auto" } : {}) }} />;
  return (
    <tbody>
      {Array.from({ length: 7 }, (_, i) => (
        <tr key={i}>
          <td>{c("10rem")}<Skeleton style={{ width: "7rem", marginTop: "var(--sk-sp-1)" }} /></td>
          <td>{c("4rem")}</td><td>{c("4rem")}</td>
          <TdNum>{c("2rem", true)}</TdNum><TdNum>{c("2rem", true)}</TdNum><TdNum>{c("2rem", true)}</TdNum><TdNum>{c("2rem", true)}</TdNum>
          <td>{c("5rem")}</td><td />
        </tr>
      ))}
    </tbody>
  );
}

export function ImplantacionesPage() {
  const engagements = useEngagements();
  const navigate = useNavigate();
  const say = useToast();
  const search = useSearch({ strict: false }) as { estado?: string };
  const [q, setQ] = useState("");
  const [fEstado, setFEstado] = useState("");
  const [fFuente, setFFuente] = useState("");
  const [orden, setOrden] = useState<{ k: SortKey; dir: 1 | -1 }>({ k: "ts", dir: -1 });
  const [pag, setPag] = useState(1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useSlashFocus();

  const data = engagements.data;
  const todas = useMemo<Row[]>(() => (data ?? []).map((e) => {
    const seed = casaDe(e.engagementId);
    return { ...e, nombre: nombreCasa(e.engagementId, e.companyName), meta: metaCasa(e), bc: seed.bc ?? false, pbi: seed.pbi ?? false, estadoCasa: estadoDe(e) };
  }), [data]);
  const filtradas = useMemo(() => {
    const query = q.trim().toLowerCase();
    const xs = todas.filter((r) =>
      (!query || r.nombre.toLowerCase().includes(query) || r.engagementId.includes(query)) &&
      (!fEstado || r.estadoCasa === fEstado) &&
      (!fFuente || (fFuente === "bc" ? r.bc : fFuente === "pbi" ? r.pbi : !r.bc && !r.pbi)));
    const val = (r: Row) => (orden.k === "nombre" ? r.nombre : orden.k === "ts" ? (r.ultimaActividad ?? "") : r[orden.k]);
    return xs.sort((a, b) => { const va = val(a), vb = val(b); return (va < vb ? -1 : va > vb ? 1 : 0) * orden.dir; });
  }, [todas, q, fEstado, fFuente, orden]);

  const pags = Math.max(1, Math.ceil(filtradas.length / POR_PAG));
  const pagReal = Math.min(pag, pags);
  const visibles = filtradas.slice((pagReal - 1) * POR_PAG, (pagReal - 1) * POR_PAG + POR_PAG);
  // la altura del contenedor se fija tras el primer pintado: al paginar no salta
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (el && !el.style.minHeight && visibles.length === POR_PAG) el.style.minHeight = `${el.offsetHeight}px`;
  }, [visibles.length]);

  // páginas a pintar, con elipsis cuando pasan de 7 (el paginador no crece sin cota)
  const paginas = useMemo<Array<number | "…">>(() => {
    const out: Array<number | "…"> = [];
    if (pags <= 7) { for (let i = 1; i <= pags; i++) out.push(i); }
    else {
      out.push(1);
      if (pagReal > 3) out.push("…");
      for (let i = Math.max(2, pagReal - 1); i <= Math.min(pags - 1, pagReal + 1); i++) out.push(i);
      if (pagReal < pags - 2) out.push("…");
      out.push(pags);
    }
    return out;
  }, [pags, pagReal]);

  const sort = (k: SortKey) => { setPag(1); setOrden((o) => (o.k === k ? { k, dir: o.dir === 1 ? -1 : 1 } : { k, dir: k === "nombre" ? 1 : -1 })); };
  const sorted = (k: SortKey): SortDir | null => (orden.k === k ? (orden.dir < 0 ? "descending" : "ascending") : null);

  const forced = search.estado;
  const view = forced === "vacio" || forced === "cargando" || forced === "error" ? forced
    : engagements.isPending ? "cargando" : engagements.isError && !data ? "error-sin-datos" : engagements.isError ? "error"
    : (data?.length ?? 0) === 0 ? "vacio" : "lista";
  const interactive = view === "lista";
  const nAct = todas.filter((r) => r.estadoCasa === "activa").length;
  const nOnb = todas.filter((r) => r.estadoCasa === "onboarding").length;
  const nPar = todas.filter((r) => r.estadoCasa === "parada").length;
  const abrir = (id: string) => void navigate({ to: "/implantaciones/$id", params: { id } });

  const tabla = (
    <Table wrapRef={wrapRef} stale={view.startsWith("error")} busy={view === "cargando"} label={view === "cargando" ? "Cargando implantaciones" : "Implantaciones"}>
      <thead>{interactive ? (
        <tr>
          <Th sortable sorted={sorted("nombre")} onSort={() => sort("nombre")}>Implantación</Th>
          <Th>Estado</Th><Th>Fuentes</Th>
          <Th num sortable sorted={sorted("criterios")} onSort={() => sort("criterios")}>Criterios</Th>
          <Th num sortable sorted={sorted("pendientesDeConfirmar")} onSort={() => sort("pendientesDeConfirmar")}>Pendientes</Th>
          <Th num sortable sorted={sorted("decisiones")} onSort={() => sort("decisiones")}>Decisiones</Th>
          <Th num sortable sorted={sorted("followUpsAbiertos")} onSort={() => sort("followUpsAbiertos")}>Seguimientos</Th>
          <Th sortable sorted={sorted("ts")} onSort={() => sort("ts")}>Última actividad</Th>
          <Th aria-label="Acciones" />
        </tr>
      ) : HEADS_PLAIN}</thead>
      {view === "cargando" ? <SkeletonRows /> : (
        <tbody>
          {visibles.map((r) => (
            <tr key={r.engagementId} tabIndex={interactive ? 0 : undefined} onClick={interactive ? () => abrir(r.engagementId) : undefined}
              onKeyDown={interactive ? (e) => { if (e.key === "Enter" && e.target === e.currentTarget) abrir(r.engagementId); } : undefined}>
              <CellTitle title={r.nombre} sub={r.meta} />
              <td><ChipEstado e={r.estadoCasa} /></td>
              <td><ChipFuentes r={r} /></td>
              <TdNum>{r.criterios || "—"}</TdNum>
              <TdNum>{r.pendientesDeConfirmar ? <Count hot>{r.pendientesDeConfirmar}</Count> : "—"}</TdNum>
              <TdNum>{r.decisiones || "—"}</TdNum>
              <TdNum>{r.followUpsAbiertos || "—"}</TdNum>
              <td><Mono><Fainted>{fechaTabla(r.ultimaActividad, r.estadoCasa === "parada")}</Fainted></Mono></td>
              {interactive ? (
                <RowActions><Button variant="quiet" size="s" onClick={(e) => { e.stopPropagation(); abrir(r.engagementId); }}>Abrir</Button></RowActions>
              ) : <td />}
            </tr>
          ))}
          {visibles.length === 0 && view === "lista" && (
            <tr><td colSpan={9}><Empty inline title="Nada casa con ese filtro." style={{ margin: "var(--sk-sp-3)" }}>Prueba a quitar el estado o la fuente.</Empty></td></tr>
          )}
        </tbody>
      )}
    </Table>
  );

  return (
    <>
      <PageHead
        title="Implantaciones"
        meta={view === "vacio" ? <Badge>0 casas</Badge>
          : !data ? (view === "cargando" ? <Skeleton style={{ width: "18rem", height: "1.25em" }} /> : undefined)
          : <>
            <Badge>{data.length} casas</Badge>
            {nAct > 0 && <Badge tone="ok">{nAct} activas</Badge>}
            {nOnb > 0 && <Badge tone="accent">{nOnb} en onboarding</Badge>}
            {nPar > 0 && <Badge tone="warn">{nPar} sin actividad 14d</Badge>}
          </>}
        actions={<Button variant="primary" onClick={() => say("El alta de implantación (empresa, primer directivo/a y provisión del asesor) llega con F5b.")}>Nueva implantación…</Button>}
      />
      <div className="listzone">
        {view !== "vacio" && view !== "error-sin-datos" && (
          <Toolbar>
            <SearchInput inputRef={inputRef} style={{ flex: 1, minWidth: "14rem" }} placeholder="Buscar por empresa o id…" aria-label="Buscar implantaciones" shortcut="/" value={q} onChange={(e) => { setQ(e.target.value); setPag(1); }} />
            <Select ariaLabel="Filtrar por estado" style={{ width: "13rem" }} value={fEstado} onChange={(v) => { setFEstado(v); setPag(1); }} options={ESTADOS} />
            <Select ariaLabel="Filtrar por fuente" style={{ width: "13rem" }} value={fFuente} onChange={(v) => { setFFuente(v); setPag(1); }} options={FUENTES} />
          </Toolbar>
        )}
        {view === "vacio" && (
          <Empty glyph="✳" title="Aún no hay implantaciones">
            Una implantación es un asesor viviendo en una casa: su directivo/a, su memoria y sus fuentes.
            Cuando des de alta la primera, vivirá aquí con sus números.
            <Button variant="primary" onClick={() => say("El alta de la primera implantación llega con F5b.")}>Dar de alta la primera…</Button>
          </Empty>
        )}
        {view === "error-sin-datos" && (
          <Banner tone="danger"><b>No llego al registro de implantaciones.</b> Y aún no tengo una copia que enseñarte. Reintento solo cada 30 s — o fuerza uno ahora. <Button variant="link" onClick={() => void engagements.refetch()}>reintentar ahora</Button></Banner>
        )}
        {view === "error" && (
          <Banner tone="danger"><b>No llego al registro de implantaciones.</b> La lista que ves puede estar desactualizada. Reintento solo cada 30 s — o fuerza uno ahora. <Button variant="link" onClick={() => void engagements.refetch()}>reintentar ahora</Button></Banner>
        )}
        {(view === "lista" || view === "cargando" || view === "error") && (
          <section>
            {tabla}
            {view === "lista" && (
              <div className="list-foot" style={{ marginTop: "var(--sk-sp-4)" }}>
                <Pagination meta={filtradas.length ? `${(pagReal - 1) * POR_PAG + 1}–${Math.min(pagReal * POR_PAG, filtradas.length)} de ${filtradas.length} · ${POR_PAG} por página` : `0 de ${data?.length ?? 0}`}>
                  <PageBtn disabled={pagReal <= 1} onClick={() => setPag(pagReal - 1)} aria-label="Página anterior">‹</PageBtn>
                  {paginas.map((p, i) => (p === "…" ? <PageEllipsis key={`e${i}`} /> : <PageBtn key={p} current={p === pagReal} onClick={() => setPag(p)}>{p}</PageBtn>))}
                  <PageBtn disabled={pagReal >= pags} onClick={() => setPag(pagReal + 1)} aria-label="Página siguiente">›</PageBtn>
                </Pagination>
              </div>
            )}
          </section>
        )}
      </div>
    </>
  );
}
